import type { PlanTier, SubscriptionStateDto } from "@/services/types";

export type BillingPlanKind = "TRIAL" | "FREE" | "PROFESSIONAL" | "TEAM";

/** A trial is active only when the backend status and resolved entitlement agree. */
export function isActiveTrial(
  state: Pick<SubscriptionStateDto, "status" | "entitled"> | null | undefined,
): boolean {
  return state?.status === "TRIAL" && state.entitled;
}

/**
 * Customer-facing plan identity follows resolved entitlement, not the catalog fallback.
 *
 * <p>`state.entitled` is a boolean and so can only say paid or unpaid; which paid tier comes
 * from `tier`. That distinction matters beyond naming: a member of a paid team is entitled
 * through somebody else's subscription, so their own state can look unpaid while their tier is
 * TEAM. `tier` is optional so callers holding only a subscription state still compile — without
 * it the answer degrades to the old split rather than guessing.
 */
export function billingPlanKind(
  state: Pick<SubscriptionStateDto, "status" | "entitled"> | null | undefined,
  tier?: PlanTier | null,
): BillingPlanKind {
  if (isActiveTrial(state)) return "TRIAL";
  // Enterprise has no separate customer-facing identity yet; it reads as Team, which is what it
  // mirrors in the catalog.
  if (tier === "TEAM" || tier === "ENTERPRISE") return "TEAM";
  if (tier === "PROFESSIONAL") return "PROFESSIONAL";
  if (tier === "FREE") return "FREE";
  return state?.entitled ? "PROFESSIONAL" : "FREE";
}

/** Percentage used by the trial ring; zero days must not leave a decorative active sliver. */
export function trialProgressPercent(daysLeft: number, totalDays: number): number {
  if (daysLeft <= 0) return 0;
  const total = totalDays > 0 ? totalDays : daysLeft;
  return Math.max(4, Math.min(100, Math.round((daysLeft / total) * 100)));
}
